import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, ElementRef, inject, input, InputSignal, viewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { CustomRouteService } from 'src/app/pages/custom-route/services/custom-route.service';
import { PlannerDayStateService } from 'src/app/pages/custom-route/shared/planner/services/planner-day-state.service';
import { factorHeightDuration } from '../../../../config/constants';
import { CardStatus } from '../../../../interfaces';
import { PointType, Transport } from 'src/app/pages/custom-route/interfaces/custom-route.interface';
import { BottomSheetConfig, BottomSheetData } from 'src/app/interfaces/mat.interface';
import { NewDetailsPage } from 'src/app/pages/new-details/new-details.page';
import { BreakpointsService } from 'src/app/services/breakpoints.service';
import { TransportListCardComponent } from "./components/transport-list-card/transport-list-card.component";
import { MatBottomSheetService } from 'src/app/services/mat-bottom-sheet.service';
import { HeaderComponent } from './components/header/header.component';
import { MainComponent } from './components/main/main.component';


@Component({
  selector: 'app-card',
  standalone: true,
  imports: [
    TranslateModule,
    TransportListCardComponent,
    HeaderComponent,
    MainComponent
],
  templateUrl: './card.component.html',
  styleUrls: ['./card.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class CardComponent  {
  public card = viewChild.required<ElementRef>('card');
  
  public indexCard: InputSignal<number> = input.required<number>();
  public id: InputSignal<number> = input.required<number>();
  public type: InputSignal<PointType> = input.required<PointType>();
  public img: InputSignal<string> = input.required<string>();
  public title: InputSignal<string> = input.required<string>(); // TODO: CAMBIAR A OBJETO DE TRADUCCIONES
  public previousPointTitle: InputSignal<string> = input.required<string>(); // TODO: CAMBIAR A OBJETO DE TRADUCCIONES
  public startDate: InputSignal<Date> = input.required<Date>();
  public durationMinutes: InputSignal<number> = input.required<number>();
  public transportList : InputSignal<Transport[]> = input.required<Transport[]>();
  public status: InputSignal<CardStatus> = input.required<CardStatus>();

  private _customRouteService = inject(CustomRouteService);
  private _plannerDayStateService = inject(PlannerDayStateService);
  private _breakpointsService = inject(BreakpointsService);
  private _bottomSheetService = inject(MatBottomSheetService);
  
  private readonly _customRouteData = computed(() => this._customRouteService.customRouteData());
  get dailyCustomRouteData() {
    return this._customRouteData().dailyRoutes[this._dayToShowIndex()];
  }
  get dailyPoints() {
    return this.dailyCustomRouteData.points;
  }
  
  private readonly _dayToShowIndex = computed(() => this._plannerDayStateService.dayToShowIndex());
  get dayToShowIndex() {
    return this._dayToShowIndex();
  }
  
  private readonly _isMobile = computed(() => this._breakpointsService.isMobile());
  get isMobile(): boolean {
    return this._isMobile();
  }
  
  public marginTop: number;
  public topPositionTransportList: number;
  public isImgError: boolean = false;
  
  setTheHeightAccordingToTheDuration = effect(() => {
    this.card().nativeElement.style.height = this.isLocationCard() ? 'fit-content' : this.durationMinutes() * factorHeightDuration + 'px';
  }, { allowSignalWrites: true });
  
  setTheMarginTopAccordingToTheStartTime = effect(() => {
    if (!this.dailyCustomRouteData || !this.dailyPoints || !this.dailyPoints.length) return;
    
    const cardElement = this.card().nativeElement;
    
    if (!cardElement) return;
    
    // convert to hours (e.g., 13.5 for 1:30 PM)
    const startDate = this.startDate(); 
    if (!startDate) return;
    const startTimeValue = startDate.getHours() + startDate.getMinutes() / 60 === 0
      ? 24
      : startDate.getHours() + startDate.getMinutes() / 60;

    if (this.indexCard() === 0) {
      const offsetMinutes = (startTimeValue - 9) * 60;
      this.marginTop = offsetMinutes * factorHeightDuration > 48 ? offsetMinutes * factorHeightDuration + 24 : 24;
      this.topPositionTransportList =  this.isTransportListOverflow() 
        ? -(this.marginTop + 12 + 6) 
        : this.marginTop + 12 < 48 
          ? -48
          : -(this.marginTop + 12); // 6px gap between card and transport list
      cardElement.style.marginTop = this.marginTop + 'px';
      
    } else {
      const previousCard = this.dailyPoints[this.indexCard() - 1];
      if (!previousCard) return; 
      
      const prevStart = previousCard.startDate.getHours() + previousCard.startDate.getMinutes() / 60;
      const prevDuration = previousCard.duration; 

      const prevEnd = prevStart + (prevDuration / 60);  
      const currentStart = startTimeValue; 

      const gapInMinutes = (currentStart - prevEnd) * 60;

      this.marginTop = gapInMinutes * factorHeightDuration - 16; // minus gap between cards

      this.topPositionTransportList = this.isTransportListOverflow() 
      ? -(this.marginTop + 6)
      : this.marginTop + 12 < 48 
        ? -48
        : -(this.marginTop + 12); // 6px gap between card and transport list

      cardElement.style.marginTop = `${this.marginTop}px`;
    }
  }, { allowSignalWrites: true });
  
  hideDetailsViewOnResizeViewport = effect(() => {
    if (this.isMobile) {
      this._bottomSheetService.closeAllMatBottomSheets();
    } else {
      this._bottomSheetService.closeAllMatBottomSheets();
    }
  }, {  allowSignalWrites: true });
  
  public async handleClickPoint(id: number): Promise<void> {
    if (this.isMobile) {
      await this.openBottomSheetDetailsMobile(id);
    } else {
      await this.openBottomSheetDetailsDesktop(id);
    }
  }
  
  public async openBottomSheetDetailsMobile(id: number): Promise<void> {
    const bottomSheetConfig: BottomSheetConfig<NewDetailsPage, BottomSheetData> = {
      component: NewDetailsPage,
      data: {
        id: id,
        type: this.type(),
        fromPointPlannerCustomRouteMobile: true,
        isModal: true
      },
      panelClass: 'details-bottom-sheet--mobile',
    }

    await this._bottomSheetService.openMatBottomSheet(bottomSheetConfig);
  }
  
  public async openBottomSheetDetailsDesktop(id: number): Promise<void> {
    const bottomSheetConfig: BottomSheetConfig<NewDetailsPage, BottomSheetData> = {
      component: NewDetailsPage,
      data: {
        id: id, 
        type: this.type(),
        fromPointPlannerCustomRouteDesktop: true,
        isModal: true
      },
      panelClass: 'details-bottom-sheet--desktop',
    }

    await this._bottomSheetService.openMatBottomSheet(bottomSheetConfig);
  }
  
  public isTransportListOverflow(): boolean {
    if (this.transportList().length === 1) return false;
    
    const heightTransportListContent = this.transportList().length * 48; // each transport item is 48px height
    const maxHeight = this.marginTop + 12 < 48 
      ? 48 
      : this.marginTop + 12; // 12px is the gap between line and card
    
    if (heightTransportListContent > maxHeight) {
      return true;
    } else {
      return false;
    }
  }
  
  public isDuration15Minutes(): boolean {
    return this.durationMinutes() === 15;
  }
  
  public handleImgError(): void {
    this.isImgError = true;
  }
  
  public isLocationCard(): boolean {
    return this.id() === 0;
  }
  
  /**
   * Calculates the start date for the location point (id = 0) based on the previous point's end time 
   * and the total transport duration.
   */
  getStartDate(): Date {
    const locationPoint = this.dailyPoints.find(point => point.id === 0);
    const totalTrasnportDuration = locationPoint?.transportList.reduce((total, transport) => total + transport.duration, 0) || 0;
    
    const previousPoint = this.dailyPoints[this.indexCard() - 1];

    if (!previousPoint) return this.startDate();
    const previousPointEndTime = previousPoint.endDate;
    
    const locationStartDate = new Date(previousPointEndTime.getTime() + totalTrasnportDuration * 60000); // Convert minutes to milliseconds
    if (!locationStartDate) return this.startDate();
    
    return this.id() === 0 ? locationStartDate : this.startDate();
  }
}
